// $http и $q по правилам AngularJS 1.2: .success/.error, разбор JSON, параметры запроса.
// После ответа страница обновляется: колбэки меняют данные шаблона.
import { toJson } from './filters';
import { reportError } from './report';

const JSON_START = /^\s*(\[|\{[^{])/;
const JSON_END = /[}\]]\s*$/;
const PROTECTION_PREFIX = /^\)\]\}',?\n/;

function noop() {}

// Обёртка над Promise: отказ без обработчика не попадает в консоль (как $q в AngularJS 1.2),
// после каждого колбэка запланирована проверка изменений.
export class QPromise {
    constructor(native, runtime) {
        this.$$native = native;
        this.$$runtime = runtime;
        native.catch(noop);
    }

    $$wrap(fn) {
        if (typeof fn !== 'function') {
            return undefined;
        }
        const runtime = this.$$runtime;
        return value => {
            try {
                return fn(value);
            } catch (error) {
                // Как $exceptionHandler в AngularJS: ошибка видна в консоли и отклоняет следующее обещание.
                reportError(error);
                throw error;
            } finally {
                runtime.scheduleDigest();
            }
        };
    }

    then(onFulfilled, onRejected) {
        return new QPromise(this.$$native.then(this.$$wrap(onFulfilled), this.$$wrap(onRejected)), this.$$runtime);
    }

    catch(onRejected) {
        return this.then(null, onRejected);
    }

    finally(callback) {
        const wrapped = this.$$wrap(callback);
        return new QPromise(this.$$native.then(
            value => {
                wrapped();
                return value;
            },
            reason => {
                wrapped();
                throw reason;
            },
        ), this.$$runtime);
    }
}

export function createQ(runtime) {
    const wrap = native => new QPromise(native, runtime);
    return {
        defer() {
            let resolve;
            let reject;
            const native = new Promise((res, rej) => {
                resolve = res;
                reject = rej;
            });
            return {
                promise: wrap(native),
                resolve(value) {
                    resolve(value instanceof QPromise ? value.$$native : value);
                },
                reject,
            };
        },
        when(value) {
            return wrap(Promise.resolve(value instanceof QPromise ? value.$$native : value));
        },
    };
}

function encodeUriQuery(value) {
    return encodeURIComponent(value)
        .replace(/%40/gi, '@')
        .replace(/%3A/gi, ':')
        .replace(/%24/g, '$')
        .replace(/%2C/gi, ',')
        .replace(/%20/g, '+');
}

export function buildUrl(url, params) {
    if (!params) {
        return url;
    }
    const parts = [];
    Object.keys(params).sort().forEach(key => {
        let value = params[key];
        if (value === null || value === undefined) {
            return;
        }
        if (!Array.isArray(value)) {
            value = [value];
        }
        value.forEach(item => {
            let v = item;
            if (v !== null && typeof v === 'object') {
                v = v instanceof Date ? v.toISOString() : toJson(v);
            }
            parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(v));
        });
    });
    if (!parts.length) {
        return url;
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + parts.join('&');
}

// Ответ сервера без преобразований: его хранит кеш, как в AngularJS 1.2.
function fetchRaw(url, init) {
    return fetch(url, init).then(response => response.text().then(text => {
        const headers = {};
        response.headers.forEach((value, key) => {
            headers[key.toLowerCase()] = value;
        });
        return { status: response.status, text, headers, statusText: response.statusText };
    }), () => ({ status: 0, text: undefined, headers: {}, statusText: '' }));
}

function isSuccess(status) {
    return status >= 200 && status < 300;
}

function transformResponse(text) {
    if (typeof text !== 'string') {
        return text;
    }
    const data = text.replace(PROTECTION_PREFIX, '');
    // Ошибка разбора JSON отклоняет запрос, как fromJson в AngularJS.
    return JSON_START.test(data) && JSON_END.test(data) ? JSON.parse(data) : data;
}

// config: params, headers и cache (объект с get/put/remove, например из $cacheFactory).
export function createHttp(runtime) {
    function $http(config) {
        const headers = { Accept: 'application/json, text/plain, */*' };
        if (config.data !== undefined) {
            headers['Content-Type'] = 'application/json;charset=utf-8';
        }
        Object.keys(config.headers || {}).forEach(name => {
            Object.keys(headers).filter(key => key.toLowerCase() === name.toLowerCase()).forEach(key => delete headers[key]);
            if (config.headers[name] !== undefined && config.headers[name] !== null) {
                headers[name] = config.headers[name];
            }
        });
        const data = config.data !== null && typeof config.data === 'object' ? toJson(config.data) : config.data;
        const url = buildUrl(config.url, config.params);

        // Кеш хранит сначала обещание запроса в пути, потом сам ответ: повторный запрос не уходит на сервер.
        const cache = config.method === 'GET' ? config.cache : null;
        const cached = cache ? cache.get(url) : undefined;
        let raw;
        if (cached !== undefined) {
            raw = cached && typeof cached.then === 'function' ? cached : Promise.resolve(cached);
        } else {
            raw = fetchRaw(url, { method: config.method, headers, body: data, credentials: 'same-origin' });
            if (cache) {
                cache.put(url, raw);
                raw.then(result => {
                    if (isSuccess(result.status)) {
                        cache.put(url, result);
                    } else {
                        cache.remove(url);
                    }
                });
            }
        }

        const native = raw.then(result => {
            const headerGetter = name => (name ? (result.headers[name.toLowerCase()] || null) : Object.assign({}, result.headers));
            runtime.scheduleDigest();
            const response = {
                data: transformResponse(result.text),
                status: result.status,
                headers: headerGetter,
                config,
                statusText: result.statusText,
            };
            if (isSuccess(result.status)) {
                return response;
            }
            throw response;
        });

        const promise = new QPromise(native, runtime);
        promise.success = fn => {
            promise.then(response => fn(response.data, response.status, response.headers, config));
            return promise;
        };
        promise.error = fn => {
            promise.then(null, response => fn(response.data, response.status, response.headers, config));
            return promise;
        };
        return promise;
    }

    return {
        get: (url, config) => $http(Object.assign({}, config, { method: 'GET', url })),
        post: (url, data, config) => $http(Object.assign({}, config, { method: 'POST', url, data })),
    };
}
